import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { oneOf, persistedStorage } from './storage.ts';

export const THEME_MODES = ['light', 'dark', 'system'] as const;
export const DENSITIES = ['compact', 'comfortable', 'spacious'] as const;

export type ThemeMode = (typeof THEME_MODES)[number];
export type Density = (typeof DENSITIES)[number];

export type Preferences = {
  readonly theme: ThemeMode;
  readonly density: Density;
  readonly setTheme: (mode: ThemeMode) => void;
  readonly setDensity: (density: Density) => void;
};

const DEFAULTS = { theme: 'light', density: 'spacious' } as const;

export const PREFERENCES_KEY = 'admin.preferences';

export const usePreferences = create<Preferences>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setTheme: (theme) => {
        set({ theme });
      },
      setDensity: (density) => {
        set({ density });
      },
    }),
    {
      name: PREFERENCES_KEY,
      storage: persistedStorage(),
      partialize: (state) => ({ theme: state.theme, density: state.density }),
      merge: (persisted, current) => {
        const stored = persisted as Partial<Preferences> | undefined;
        return {
          ...current,
          theme: oneOf(THEME_MODES, stored?.theme, DEFAULTS.theme),
          density: oneOf(DENSITIES, stored?.density, DEFAULTS.density),
        };
      },
    },
  ),
);

export const useDensity = (): Density => usePreferences((state) => state.density);
